import { Head, Link, useForm } from "@inertiajs/react"
import Navbar from "@/components/chalky/Navbar"
import Footer from "@/components/chalky/Footer"
import { cn } from "@/lib/utils"

interface SignUpProps {
  paths: { sign_up: string; sign_in: string }
  i18n: {
    title: string
    subtitle: string
    email_label: string
    password_label: string
    password_confirmation_label: string
    submit: string
    submitting: string
    has_account: string
    sign_in_link: string
  }
}

export default function SignUp({ paths, i18n }: SignUpProps) {
  const form = useForm({
    email_address: "",
    password: "",
    password_confirmation: "",
  })
  const { data, setData, processing, errors } = form

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    form.transform((formData) => ({ account: formData }))
    form.post(paths.sign_up)
  }

  const inputClass = (hasError: boolean) =>
    cn(
      "w-full px-4 py-3 bg-white/5 border rounded-xl text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-violet-500/50 transition-all",
      hasError ? "border-red-500/50" : "border-white/10 focus:border-violet-500/50"
    )

  return (
    <>
      <Head>
        <title>{`${i18n.title} — Chalky Chrono Finish`}</title>
      </Head>

      <div className="bg-[#0a0a0f] min-h-screen text-slate-300 flex flex-col">
        <Navbar />

        <main className="flex-1 flex items-center justify-center px-6 pt-32 pb-24">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-white mb-2">{i18n.title}</h1>
              <p className="text-slate-400">{i18n.subtitle}</p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl space-y-6"
            >
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
                  {i18n.email_label}
                </label>
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={data.email_address}
                  onChange={(e) => setData("email_address", e.target.value)}
                  required
                  className={inputClass(Boolean(errors.email_address))}
                  placeholder="vous@exemple.fr"
                />
                {errors.email_address && (
                  <p className="mt-1 text-xs text-red-400">{errors.email_address}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-300 mb-2">
                  {i18n.password_label}
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  value={data.password}
                  onChange={(e) => setData("password", e.target.value)}
                  required
                  className={inputClass(Boolean(errors.password))}
                  placeholder="••••••••"
                />
                {errors.password && (
                  <p className="mt-1 text-xs text-red-400">{errors.password}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="password_confirmation"
                  className="block text-sm font-medium text-slate-300 mb-2"
                >
                  {i18n.password_confirmation_label}
                </label>
                <input
                  id="password_confirmation"
                  type="password"
                  autoComplete="new-password"
                  value={data.password_confirmation}
                  onChange={(e) => setData("password_confirmation", e.target.value)}
                  required
                  className={inputClass(Boolean(errors.password_confirmation))}
                  placeholder="••••••••"
                />
                {errors.password_confirmation && (
                  <p className="mt-1 text-xs text-red-400">{errors.password_confirmation}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={processing}
                className="w-full py-4 text-sm font-medium text-white bg-violet-600 hover:bg-violet-500 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all duration-200"
              >
                {processing ? i18n.submitting : i18n.submit}
              </button>
            </form>

            <p className="text-center text-sm text-slate-400 mt-6">
              {i18n.has_account}{" "}
              <Link href={paths.sign_in} className="text-violet-400 hover:text-violet-300">
                {i18n.sign_in_link}
              </Link>
            </p>
          </div>
        </main>

        <Footer />
      </div>
    </>
  )
}
